/* Ecomify theme — storefront behavior. Plain JS, no dependencies. */
(() => {
  const theme = window.theme || {};
  const parser = new DOMParser();
  const parseHTML = (html) => parser.parseFromString(html, 'text/html');

  const debounce = (fn, wait = 300) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };

  const fetchJSON = (url, body) =>
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then((r) => r.json());

  /* Drawers ---------------------------------------------------------------- */

  let lastFocused = null;

  const openDrawer = (drawer) => {
    if (!drawer) return;
    lastFocused = document.activeElement;
    drawer.classList.add('is-open');
    document.body.classList.add('has-drawer-open');
    document
      .querySelectorAll(`[aria-controls="${drawer.id}"]`)
      .forEach((el) => el.setAttribute('aria-expanded', 'true'));
    const focusTarget = drawer.querySelector('input[type="search"]') || drawer.querySelector('[data-drawer-close]:not(.drawer__overlay)');
    setTimeout(() => focusTarget && focusTarget.focus(), 50);
  };

  const closeDrawer = (drawer) => {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    if (!document.querySelector('.drawer.is-open')) document.body.classList.remove('has-drawer-open');
    document
      .querySelectorAll(`[aria-controls="${drawer.id}"]`)
      .forEach((el) => el.setAttribute('aria-expanded', 'false'));
    if (lastFocused) lastFocused.focus();
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-drawer-open]');
    if (opener) {
      const drawer = document.getElementById(opener.dataset.drawerOpen);
      if (drawer) {
        event.preventDefault();
        openDrawer(drawer);
      }
      return;
    }
    const closer = event.target.closest('[data-drawer-close]');
    if (closer) closeDrawer(closer.closest('.drawer'));

    // Close open dropdown menus when clicking elsewhere.
    document.querySelectorAll('details[data-close-on-outside][open]').forEach((details) => {
      if (!details.contains(event.target)) details.removeAttribute('open');
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const drawer = document.querySelector('.drawer.is-open');
    if (drawer) return closeDrawer(drawer);
    document.querySelectorAll('details[data-close-on-outside][open]').forEach((details) => {
      details.removeAttribute('open');
      details.querySelector('summary').focus();
    });
  });

  // Open header dropdowns on hover for mouse users; clicks still toggle them.
  const hoverQuery = window.matchMedia('(hover: hover) and (min-width: 990px)');
  document.querySelectorAll('details[data-hover-open]').forEach((details) => {
    let closeTimer;
    const item = details.closest('li') || details;
    item.addEventListener('mouseenter', () => {
      if (!hoverQuery.matches) return;
      clearTimeout(closeTimer);
      document.querySelectorAll('details[data-hover-open][open]').forEach((other) => {
        if (other !== details) other.removeAttribute('open');
      });
      details.setAttribute('open', '');
    });
    item.addEventListener('mouseleave', () => {
      if (!hoverQuery.matches) return;
      closeTimer = setTimeout(() => details.removeAttribute('open'), 180);
    });
  });

  // Keep focus inside an open drawer.
  document.addEventListener('focusin', (event) => {
    const drawer = document.querySelector('.drawer.is-open');
    if (drawer && !drawer.contains(event.target)) {
      const first = drawer.querySelector('button, a, input, select, textarea');
      if (first) first.focus();
    }
  });

  /* Small helpers ---------------------------------------------------------- */

  // Collapse filters on small screens.
  if (window.matchMedia('(max-width: 749px)').matches) {
    document.querySelectorAll('[data-open-on-desktop]').forEach((d) => d.removeAttribute('open'));
  }

  // Auto-submit selects (country / language).
  document.addEventListener('change', (event) => {
    if (event.target.matches('select[data-autosubmit]')) event.target.form.submit();
  });

  /* Quantity input --------------------------------------------------------- */

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelectorAll('button').forEach((button) =>
        button.addEventListener('click', () => {
          const step = button.name === 'plus' ? 1 : -1;
          const min = Number(this.input.min || 0);
          this.input.value = Math.max(min, Number(this.input.value || 0) + step);
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        })
      );
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* Cart ------------------------------------------------------------------- */

  const cartSectionIds = () =>
    Array.from(document.querySelectorAll('[data-cart-section]')).map((el) => el.dataset.cartSection);

  const updateCartCount = (count) => {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count > 0 ? count : '';
    });
  };

  const renderCartSections = (sections) => {
    if (!sections) return;
    Object.entries(sections).forEach(([id, html]) => {
      if (!html) return;
      const current = document.querySelector(`[data-cart-section="${id}"]`);
      const fresh = parseHTML(html).querySelector(`[data-cart-section="${id}"]`);
      if (!current || !fresh) return;
      // Keep the drawer element (and its open state); swap only its inner panel.
      if (current.classList.contains('drawer')) {
        current.querySelector('[data-cart-drawer-inner]').replaceWith(fresh.querySelector('[data-cart-drawer-inner]'));
      } else {
        current.innerHTML = fresh.innerHTML;
      }
    });
  };

  const changeLine = (line, quantity) => {
    document.querySelectorAll('[data-cart-section]').forEach((el) => el.setAttribute('aria-busy', 'true'));
    return fetchJSON(`${theme.routes.cartChange}.js`, { line, quantity, sections: cartSectionIds() })
      .then((cart) => {
        if (cart.status) throw new Error(cart.description || cart.message);
        updateCartCount(cart.item_count);
        renderCartSections(cart.sections);
      })
      .catch((error) => alert(error.message || theme.strings.cartError))
      .finally(() => document.querySelectorAll('[data-cart-section]').forEach((el) => el.removeAttribute('aria-busy')));
  };

  document.addEventListener(
    'change',
    debounce((event) => {
      const qty = event.target.closest('[data-cart-quantity]');
      if (qty) changeLine(Number(qty.dataset.line), Number(event.target.value));
    }, 350)
  );

  document.addEventListener('click', (event) => {
    const remove = event.target.closest('[data-cart-remove]');
    if (!remove) return;
    event.preventDefault();
    changeLine(Number(remove.dataset.line), 0);
  });

  document.addEventListener(
    'input',
    debounce((event) => {
      if (event.target.matches('[data-cart-note]')) {
        fetchJSON(`${theme.routes.cart}/update.js`, { note: event.target.value });
      }
    }, 500)
  );

  // Add to cart for every product form (product page and quick add).
  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-product-form]');
    if (!form) return;
    const drawer = document.getElementById('CartDrawer');
    if (theme.cartType !== 'drawer' || !drawer) return; // Fall back to a normal form post.

    event.preventDefault();
    const button = event.submitter || form.querySelector('[type="submit"]');
    const errorEl = form.querySelector('[data-form-error]');
    if (button.disabled || button.classList.contains('is-loading')) return;
    button.classList.add('is-loading');
    if (errorEl) errorEl.textContent = '';

    const body = new FormData(form, event.submitter && event.submitter.name ? event.submitter : undefined);
    body.append('sections', cartSectionIds().join(','));
    body.append('sections_url', window.location.pathname);

    fetch(`${theme.routes.cartAdd}.js`, { method: 'POST', headers: { Accept: 'application/json' }, body })
      .then((r) => r.json())
      .then((response) => {
        if (response.status) {
          if (errorEl) errorEl.textContent = response.description || response.message;
          else alert(response.description || response.message);
          return;
        }
        renderCartSections(response.sections);
        return fetch(`${theme.routes.cart}.js`)
          .then((r) => r.json())
          .then((cart) => {
            updateCartCount(cart.item_count);
            const quickView = document.getElementById('QuickViewDrawer');
            if (quickView && quickView.classList.contains('is-open')) quickView.classList.remove('is-open');
            openDrawer(document.getElementById('CartDrawer'));
          });
      })
      .catch(() => errorEl && (errorEl.textContent = theme.strings.cartError))
      .finally(() => button.classList.remove('is-loading'));
  });

  /* Variant picker --------------------------------------------------------- */

  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
      this.addEventListener('change', this.onChange.bind(this));
    }

    selectedOptions() {
      return Array.from(this.querySelectorAll('fieldset')).map((fieldset) => {
        const checked = fieldset.querySelector('input:checked');
        const label = fieldset.querySelector('[data-selected-value]');
        if (label && checked) label.textContent = checked.value;
        return checked ? checked.value : null;
      });
    }

    onChange() {
      const options = this.selectedOptions();
      const variant = this.variants.find((v) => v.options.every((value, i) => value === options[i]));
      const info = this.closest('product-info');
      const button = info.querySelector('[data-product-form] [type="submit"]');

      if (!variant) {
        if (button) {
          button.disabled = true;
          button.textContent = theme.strings.unavailable;
        }
        return;
      }

      const url = new URL(info.dataset.productUrl, window.location.origin);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);

      url.searchParams.set('section_id', info.dataset.sectionId);
      fetch(url)
        .then((r) => r.text())
        .then((html) => {
          const doc = parseHTML(html);
          info.querySelectorAll('[data-update]').forEach((el) => {
            const fresh = doc.querySelector(`[data-update="${el.dataset.update}"]`);
            if (fresh) el.innerHTML = fresh.innerHTML;
          });
          // Refresh availability state on option values.
          const freshPicker = doc.querySelector('variant-picker');
          if (freshPicker) {
            freshPicker.querySelectorAll('input').forEach((input) => {
              const current = this.querySelector(`#${CSS.escape(input.id)}`);
              if (current) current.classList.toggle('is-unavailable', input.classList.contains('is-unavailable'));
            });
          }
        });

      if (variant.featured_media) this.showMedia(info, variant.featured_media.id);
    }

    showMedia(info, mediaId) {
      const list = info.querySelector('[data-media-list]');
      const item = list && list.querySelector(`[data-media-id="${mediaId}"]`);
      if (!item) return;
      list.prepend(item);
      if (window.matchMedia('(max-width: 749px)').matches) list.scrollTo({ left: 0, behavior: 'smooth' });
    }
  }
  customElements.define('variant-picker', VariantPicker);

  /* Product recommendations ------------------------------------------------ */

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.children.length) return;
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries[0].isIntersecting) return;
          observer.disconnect();
          fetch(this.dataset.url)
            .then((r) => r.text())
            .then((html) => {
              const fresh = parseHTML(html).querySelector('product-recommendations');
              if (fresh && fresh.innerHTML.trim()) this.innerHTML = fresh.innerHTML;
            });
        },
        { rootMargin: '0px 0px 400px 0px' }
      );
      observer.observe(this);
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);

  /* Predictive search ------------------------------------------------------ */

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      if (this.hasAttribute('data-disabled')) return;
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-search-results]') || this.querySelector('#PredictiveSearchResults');
      this.input.addEventListener('input', debounce(this.search.bind(this), 250));
      // Inline (header) search: close the results on Escape or a click outside.
      if (this.hasAttribute('data-inline')) {
        document.addEventListener('click', (event) => { if (!this.contains(event.target)) this.toggleAttribute('open', false); });
        this.addEventListener('keydown', (event) => { if (event.key === 'Escape') this.toggleAttribute('open', false); });
        this.input.addEventListener('focus', () => this.toggleAttribute('open', this.results.innerHTML.trim() !== ''));
      }
    }

    search() {
      const q = this.input.value.trim();
      if (!q) {
        this.results.innerHTML = '';
        this.toggleAttribute('open', false);
        return;
      }
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      const url = `${theme.routes.predictiveSearch}?q=${encodeURIComponent(q)}&section_id=predictive-search&resources[limit]=6`;
      fetch(url, { signal: this.controller.signal })
        .then((r) => r.text())
        .then((html) => {
          const fresh = parseHTML(html).querySelector('[data-predictive-results]');
          this.results.innerHTML = fresh ? fresh.innerHTML : '';
          this.toggleAttribute('open', this.results.innerHTML.trim() !== '');
        })
        .catch(() => {});
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* Facets: reload with the new filters ------------------------------------ */

  class FacetFilters extends HTMLElement {
    connectedCallback() {
      const form = this.querySelector('form');
      const apply = () => {
        const params = new URLSearchParams(new FormData(form));
        for (const [key, value] of Array.from(params.entries())) {
          if (value === '') params.delete(key);
        }
        window.location.search = params.toString();
      };
      form.addEventListener('change', (event) => {
        if (event.target.type === 'number') return;
        apply();
      });
      form.addEventListener('input', debounce((event) => event.target.type === 'number' && apply(), 900));
    }
  }
  customElements.define('facet-filters', FacetFilters);

  /* Share ------------------------------------------------------------------ */

  class ShareButton extends HTMLElement {
    connectedCallback() {
      const button = this.querySelector('button');
      button.addEventListener('click', () => {
        const { shareUrl, shareTitle } = button.dataset;
        if (navigator.share) {
          navigator.share({ url: shareUrl, title: shareTitle }).catch(() => {});
        } else if (navigator.clipboard) {
          navigator.clipboard.writeText(shareUrl);
        }
      });
    }
  }
  customElements.define('share-button', ShareButton);

  /* Countdown -------------------------------------------------------------- */

  class CountDown extends HTMLElement {
    connectedCallback() {
      const end = new Date(this.dataset.end.trim().replace(' ', 'T'));
      if (Number.isNaN(end.getTime())) return;
      this.end = end.getTime();
      this.values = {};
      this.querySelectorAll('[data-unit]').forEach((el) => (this.values[el.dataset.unit] = el));
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    tick() {
      const left = Math.max(0, this.end - Date.now());
      const pad = (n) => String(n).padStart(2, '0');
      this.values.days.textContent = pad(Math.floor(left / 86400000));
      this.values.hours.textContent = pad(Math.floor((left / 3600000) % 24));
      this.values.minutes.textContent = pad(Math.floor((left / 60000) % 60));
      this.values.seconds.textContent = pad(Math.floor((left / 1000) % 60));
      if (left === 0) {
        clearInterval(this.timer);
        if (this.dataset.hideWhenDone === 'true') this.hidden = true;
      }
    }
  }
  customElements.define('count-down', CountDown);

  /* Collection tabs -------------------------------------------------------- */

  class CollectionTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.select(index));
        tab.addEventListener('keydown', (event) => {
          const step = { ArrowRight: 1, ArrowLeft: -1 }[event.key];
          if (!step) return;
          event.preventDefault();
          const next = (index + step + this.tabs.length) % this.tabs.length;
          this.select(next);
          this.tabs[next].focus();
        });
      });
    }

    select(index) {
      this.tabs.forEach((tab, i) => {
        const selected = i === index;
        tab.setAttribute('aria-selected', selected);
        tab.tabIndex = selected ? 0 : -1;
        this.querySelector(`#${CSS.escape(tab.getAttribute('aria-controls'))}`).hidden = !selected;
      });
    }
  }
  customElements.define('collection-tabs', CollectionTabs);

  /* Showcase switcher ------------------------------------------------------ */

  class ShowcaseSwitcher extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.url = this.querySelector('[data-showcase-url]');
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.select(index));
        tab.addEventListener('keydown', (event) => {
          const step = { ArrowRight: 1, ArrowLeft: -1 }[event.key];
          if (!step) return;
          event.preventDefault();
          const next = (index + step + this.tabs.length) % this.tabs.length;
          this.select(next);
          this.tabs[next].focus();
        });
      });
    }

    select(index) {
      this.tabs.forEach((tab, i) => {
        const selected = i === index;
        tab.setAttribute('aria-selected', selected);
        tab.tabIndex = selected ? 0 : -1;
        const panel = this.querySelector(`#${CSS.escape(tab.getAttribute('aria-controls'))}`);
        panel.hidden = !selected;
        if (selected && this.url) this.url.textContent = panel.dataset.url;
      });
    }
  }
  customElements.define('showcase-switcher', ShowcaseSwitcher);

  /* Article table of contents ---------------------------------------------- */

  const buildToc = () => {
    const content = document.querySelector('[data-article-content]');
    const list = document.querySelector('[data-toc-list]');
    if (!content || !list) return;
    const headings = Array.from(content.querySelectorAll('h2'));
    if (headings.length < 3) {
      const page = content.closest('.has-toc');
      if (page) page.classList.remove('has-toc');
      return;
    }
    const used = new Set();
    headings.forEach((heading) => {
      let id = heading.id || heading.textContent.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      while (used.has(id) || (!heading.id && document.getElementById(id))) id += '-1';
      used.add(id);
      heading.id = id;
      const item = document.createElement('li');
      const link = document.createElement('a');
      link.href = `#${id}`;
      link.textContent = heading.textContent.trim();
      item.append(link);
      list.append(item);
    });
    list.closest('.article-toc').hidden = false;
    if (window.matchMedia('(max-width: 1099px)').matches) list.closest('details').removeAttribute('open');

    const links = Array.from(list.querySelectorAll('a'));
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          links.forEach((a) => a.setAttribute('aria-current', a.hash === `#${entry.target.id}` ? 'true' : 'false'));
        });
      },
      { rootMargin: '0px 0px -70% 0px' }
    );
    headings.forEach((h) => observer.observe(h));
  };
  buildToc();

  /* Section x-ray ----------------------------------------------------------- */

  const sectionName = (section) => {
    const cls = Array.from(section.classList).find((c) => c.startsWith('section-') || c.startsWith('shopify-section--'));
    const key = cls ? cls.replace(/^(shopify-)?section-+(main-)?/, '') : (section.id.split('__')[1] || '');
    if (!key) return null;
    const name = key.replace(/[-_]+/g, ' ').replace(/\s*\d+$/, '');
    return name.charAt(0).toUpperCase() + name.slice(1);
  };

  const setXray = (on) => {
    document.documentElement.classList.toggle('xray', on);
    document.querySelectorAll('[data-xray-toggle]').forEach((b) => b.setAttribute('aria-pressed', on));
    if (!on) return;
    document.querySelectorAll('.shopify-section').forEach((section) => {
      if (section.querySelector(':scope > .xray-label')) return;
      const name = sectionName(section);
      if (!name) return;
      const label = document.createElement('span');
      label.className = 'xray-label';
      label.textContent = name;
      section.prepend(label);
    });
  };

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-xray-toggle]');
    if (!toggle) return;
    const on = toggle.getAttribute('aria-pressed') !== 'true';
    setXray(on);
    try {
      sessionStorage.setItem('xray', on ? '1' : '0');
    } catch (e) {}
  });

  try {
    if (sessionStorage.getItem('xray') === '1') setXray(true);
  } catch (e) {}

  window.themeKit = { openDrawer, closeDrawer, parseHTML, debounce, cartSectionIds, renderCartSections, updateCartCount };

  /* Links to other websites ------------------------------------------------- */

  if (document.body.hasAttribute('data-external-new-tab')) {
    const markExternal = (root) => {
      root.querySelectorAll('a[href^="http"]').forEach((link) => {
        if (link.hostname && link.hostname !== location.hostname && !link.target) {
          link.target = '_blank';
          link.rel = `${link.rel} noopener`.trim();
        }
      });
    };
    markExternal(document);
    new MutationObserver((records) => records.forEach((r) => r.addedNodes.forEach((n) => n.nodeType === 1 && markExternal(n))))
      .observe(document.body, { childList: true, subtree: true });
  }

  /* Trusted links: open in a new tab ourselves. Shopify's "leave this store" warning steps aside
     when the store's own script handles the click (it checks defaultPrevented). */

  const trusted = (document.body.dataset.trustedLinks || '').split(/\s+/).map((d) => d.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/.*$/, '')).filter(Boolean);
  if (trusted.length) {
    const isTrusted = (host) => trusted.some((d) => host === d || host.endsWith(`.${d}`));
    document.addEventListener('click', (event) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest('a[href]');
      if (!link || !/^https?:$/.test(link.protocol) || link.hostname === location.hostname || !isTrusted(link.hostname.toLowerCase())) return;
      event.preventDefault();
      window.open(link.href, '_blank', 'noopener');
    });
  }

  /* Theme editor support --------------------------------------------------- */

  document.addEventListener('shopify:section:load', () => {
    document.body.classList.remove('has-drawer-open');
  });

  // Show the tab a merchant selects in the theme editor.
  document.addEventListener('shopify:block:select', (event) => {
    const tab = document.getElementById(`Tab-${event.detail.blockId}`) || document.getElementById(`ShowcaseTab-${event.detail.blockId}`);
    if (tab) tab.click();
  });
})();
